import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useToggle } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';

import { __t } from '@mssgme/shared';
import { isLightColor } from '@mssgme/helpers';
import { useWebsiteData } from '../../hooks';
import { ServicePhotos } from './ServicePhotos';
import { ServiceActionsPane } from './ServiceActionsPane';
import { ExternalUrlButton } from './ExternalUrlButton';
import { ShoppingCart } from '../ShoppingCart';

import styles from './ServiceContent.scss';

export function ServiceContent({
    service: { description, price },
    service,
    contacts,
    isActive,
    isPrev,
    isNext,
    interactive,
    themeStyles,
    backUrl,
    onClose,
}) {
    const { enabledPayments } = useWebsiteData();
    const [isCartAddAnimation, toggleCartAddAnimation] = useToggle(false);
    const rootEl = useRef(null);
    const serviceTitle = service.title || service.name;
    const isDarkTheme = !isLightColor(themeStyles.backgroundColor);
    const style = useMemo(() => {
        const { color, backgroundColor } = themeStyles;

        return {
            common: {
                color,
                backgroundColor,
            },
            color: {
                color,
            },
            background: {
                backgroundColor,
            },
            pagination: {
                backgroundColor: backgroundColor.slice(0, -2) + '80',
                color,
            },
            contacts: {
                backgroundImage: `linear-gradient(to bottom, ${backgroundColor.substring(
                    0,
                    7
                )}00 0%, ${backgroundColor} 18px, ${backgroundColor} 100%)`,
            },
        };
    }, [themeStyles]);

    const isSwipeable = isActive || isPrev || isNext;
    const hasActions = enabledPayments || contacts.length > 0;

    const contactsEl = hasActions && (
        <ServiceActionsPane
            style={style.contacts}
            contacts={contacts}
            service={service}
            interactive={interactive}
            enabledPayments={enabledPayments}
            onCartAdd={toggleCartAddAnimation}
            currency={price?.currency}
        />
    );

    const handleClose = useCallback(
        (e) => {
            e?.preventDefault?.();

            onClose(backUrl);
        },
        [backUrl, onClose]
    );

    useEffect(() => {
        if (rootEl.current && rootEl.current.scrollTop !== 0) {
            rootEl.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [isSwipeable]);

    useEffect(() => {
        if (!isCartAddAnimation) {
            return;
        }

        const timer = setTimeout(() => toggleCartAddAnimation(false), 1000);

        return () => {
            clearTimeout(timer);
        };
    }, [isCartAddAnimation]);

    return (
        <div className={styles.wrapper}>
            <div className={cn(styles.root, { [styles.active]: isActive })} ref={rootEl} style={style.color}>
                <ServicePhotos
                    photos={service.photos}
                    title={serviceTitle}
                    isActive={isActive}
                    isPrev={isPrev}
                    isNext={isNext}
                    themeStyles={themeStyles}
                    style={style}
                    onClose={handleClose}
                />

                <div className={cn(styles.content, isDarkTheme && styles.dark)}>
                    <div>
                        <h1 className={styles.title}>{serviceTitle}</h1>

                        {price && (
                            <div className={styles.price} style={style.color}>
                                {price.formattedDiscount && (
                                    <span className={styles.discountPrice}>
                                        {price.discount === 0 ? __t('free') : price.formattedDiscount}
                                    </span>
                                )}
                                {price.amount === 0 ? (
                                    __t('free')
                                ) : (
                                    <span className={price.formattedDiscount ? styles.lineThrough : null}>
                                        {price.formatted}
                                    </span>
                                )}
                            </div>
                        )}

                        <ExternalUrlButton themeStyles={themeStyles} service={service} />
                    </div>
                    {description && <p className={styles.description}>{description}</p>}
                    {contactsEl && <div className={styles.fixedPane}>{contactsEl}</div>}

                    <AnimatePresence>
                        {isCartAddAnimation && (
                            <motion.div
                                className={styles.cartBouncer}
                                animate={{ bottom: 16 }}
                                exit={{ bottom: -60 }}
                                transition={{ type: 'spring', stiffness: 100 }}
                            >
                                <ShoppingCart style={{ position: 'absolute', bottom: 0, right: 0 }} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {contactsEl}
        </div>
    );
}

ServiceContent.propTypes = {
    service: PropTypes.object.isRequired,
    contacts: PropTypes.array.isRequired,
    isActive: PropTypes.bool.isRequired,
    isPrev: PropTypes.bool.isRequired,
    isNext: PropTypes.bool.isRequired,
    interactive: PropTypes.bool,
    themeStyles: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    backUrl: PropTypes.string,
};
